/**
 * Shared ServiceBox transfer helpers.
 *
 * Single source of truth for turning an ordre into a ServiceBox `CreateRdvParams`
 * payload and for validating that an ordre is transferable. Used by both the
 * single-ordre transfer UI (ServiceBoxTransferModal) and the batch pre-transfer
 * admin screen so the two flows can never drift.
 */
import type { Ordre } from "types/ordre";
import type { Dossier, DossierType } from "types/dossier";
import type { OrgServiceBoxSite } from "types/org";
import type { CreateRdvParams, AgendaOption, ServiceBoxCredentials } from "@services/serviceboxProxy";
import { isAPrevoir, interventionFullLabel } from "types/ordre";
import { mecParts } from "@utils/vehicleMec";
import { clientVehiclesApi } from "@services/api/clientVehicles";
import api from "@services/api/client";
import { VIN_BRANDS, vehicleMarque } from "./vehicleBrand";
import { bookedContact, knownCivilite, sbClientIdentity, sbPhone } from "./sbClientIdentity";

export { vehicleMarque };

/** Map common marque strings to a normalised key for matching against sites. */
export function normaliseMarque(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .trim();
}

/**
 * Resolve the ServiceBox brand *code* (`rdvDto.rdvMarque`) from a vehicle's marque libellé.
 * ServiceBox files the RDV under the brand this code names — NOT the free-text `marque_libelle`
 * ("Fiat", "Renault"…), which is only a display label. If unset, the proxy defaults to "AC"
 * (Citroën), which is why non-Stellantis vehicles were landing as CITROEN.
 *
 * Rule: Citroën → AC, Opel → OV, Peugeot → AP, anything else → AP (Peugeot).
 * (Brand codes confirmed from the ServiceBox create form: rdvDto.rdvMarque = AP/AC/OV.)
 */
export function marqueCode(rawMarque: string | undefined): string {
  const key = normaliseMarque(rawMarque || "");
  if (key.includes("CITROEN")) return "AC";
  if (key.includes("OPEL")) return "OV";
  return "AP"; // Peugeot + everything else
}

export function detectSite(
  sites: OrgServiceBoxSite[] | undefined,
  vehicleMarque: string | undefined,
): OrgServiceBoxSite | undefined {
  if (!sites?.length || !vehicleMarque) return undefined;
  const key = normaliseMarque(vehicleMarque);
  return sites.find((s) => normaliseMarque(s.marque) === key);
}

/**
 * The ServiceBox agenda rdvId this ordre lives on, or "" when it is not on ServiceBox.
 *
 * Front twin of the backend's `sb_status_sync.sb_rdv_id_for_push()`. The id is the same
 * one on both sides; only the field it was written into differs, depending on which way
 * the ordre travelled:
 *   - `servicebox_rdv_id`  — Carvis created the RDV on ServiceBox (transfer modal).
 *   - `sb_agenda_rdv_id`   — the RDV was imported FROM ServiceBox (the usual case here).
 * Testing `servicebox_rdv_id` alone reads every imported ordre as "not on ServiceBox",
 * which is how a re-transfer ended up asking ServiceBox to create a second RDV on top of
 * its own — refused as "CCS indisponible", or, had the slot been free, silently duplicated.
 */
export function serviceboxRdvId(
  ordre: Pick<Ordre, "servicebox_rdv_id" | "sb_agenda_rdv_id"> | undefined | null,
): string {
  return String(ordre?.servicebox_rdv_id || ordre?.sb_agenda_rdv_id || "");
}

/** Whether this ordre already exists as a RDV on ServiceBox, whichever way it got there. */
export function isOnServiceBox(
  ordre: Pick<Ordre, "servicebox_rdv_id" | "sb_agenda_rdv_id"> | undefined | null,
): boolean {
  return Boolean(serviceboxRdvId(ordre));
}

/** Normalise an intervention label/name for matching ordre interventions against the catalog. */
export function normaliseLabel(raw: string): string {
  return raw.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/** Split a free-text French address into {rue, cp, ville} for the DMS client payload.
 *  Heuristic: a 5-digit code postal separates the street from the city. */
export function parseAddress(adresse?: string): { rue: string; cp: string; ville: string } {
  const raw = (adresse || "").trim();
  if (!raw) return { rue: "", cp: "", ville: "" };
  const m = raw.match(/^(.*?)[\s,]*\b(\d{5})\b[\s,]*(.*)$/);
  if (m) return { rue: m[1].replace(/[,\s]+$/, "").trim(), cp: m[2], ville: m[3].trim() };
  return { rue: raw, cp: "", ville: "" };
}

export function formatDateYYYYMMDD(ts?: { seconds: number }): string {
  if (!ts) return "";
  const d = new Date(ts.seconds * 1000);
  return d.getFullYear().toString() +
    (d.getMonth() + 1).toString().padStart(2, "0") +
    d.getDate().toString().padStart(2, "0");
}

export function formatTimeHHMM(ts?: { seconds: number }): string {
  if (!ts) return "0900";
  const d = new Date(ts.seconds * 1000);
  return d.getHours().toString().padStart(2, "0") +
    d.getMinutes().toString().padStart(2, "0");
}

/**
 * Resolve the ServiceBox réceptionnaire (planning column = a person) for an ordre from
 * its réception category, via the org's Réceptions mapping (Paramètres → Réceptions,
 * each carrying `servicebox_receptionnaire_id`). Returns "" when unmapped so the caller
 * can fall back to a default. Mirrors ServiceBoxTransferModal's auto-default logic.
 */
export function resolveReceptionnaireId(
  category: string | undefined | null,
  orgReceptions: { id: string; servicebox_receptionnaire_id?: string }[] | undefined,
  receptionnaires: AgendaOption[],
): string {
  if (!category) return "";
  const rec = (orgReceptions || []).find((r) => r.id === category);
  const sid = rec?.servicebox_receptionnaire_id;
  return sid && receptionnaires.some((r) => r.id === sid) ? sid : "";
}

/** Translate raw ServiceBox validation errors into actionable French messages.
 *  The single copy: the transfer modal kept a private one that drifted, which is why it
 *  went on blaming the horaire for a "CCS indisponible" that was a duplicate RDV. */
export function humanizeServiceboxError(
  raw?: string,
  ctx?: { reception?: string; restitution?: string; alreadyOnServiceBox?: boolean },
): string {
  if (!raw) return "Erreur inconnue";
  if (/CCS indisponible/i.test(raw)) {
    const t = [
      ctx?.reception ? `réception ${ctx.reception}` : null,
      ctx?.restitution ? `restitution ${ctx.restitution}` : null,
    ].filter(Boolean).join(" / ");
    // Blaming the horaire when the ordre is already on ServiceBox sends people hunting for
    // an off-grid minute that has nothing to do with it: the RDV collided with ITSELF,
    // because the transfer asked for a *creation* on a column its own RDV already holds.
    if (ctx?.alreadyOnServiceBox) {
      return `Ce rendez-vous existe déjà sur ServiceBox${t ? ` (${t})` : ""} : ServiceBox a refusé d'en créer un second sur la même colonne (« CCS indisponible »). Il ne faut pas le re-transférer : passez par « Mettre à jour ServiceBox » (menu Actions de l'ordre) pour y reporter une intervention ou récupérer le numéro d'OR.`;
    }
    return `Créneau refusé par ServiceBox${t ? ` (${t})` : ""} : « CCS indisponible ». La colonne du réceptionnaire est probablement déjà occupée à cette heure, ou l'horaire est hors des plages ServiceBox (avant le premier créneau, après le dernier, ou hors d'un pas de 7,5 min).`;
  }
  if (/Mod[èe]le est obligatoire/i.test(raw)) {
    return "Le modèle du véhicule est obligatoire côté ServiceBox. Complétez la fiche véhicule.";
  }
  if (/v[ée]hicule de remplacement.*avant.*retour/i.test(raw)) {
    return "Les dates du véhicule de remplacement sont incohérentes : le départ doit précéder le retour.";
  }
  if (/T[ée]l[ée]phone mobile est obligatoire/i.test(raw)) {
    return "Téléphone mobile obligatoire côté ServiceBox — le client n'a pas de mobile renseigné.";
  }
  return raw;
}

/** A safe default ServiceBox réceptionnaire: the first org réception carrying a
 *  servicebox_receptionnaire_id that also exists in the agenda options (a real bookable
 *  column like RECEPTION 1). Preferred over the raw first agenda option, which may be a
 *  non-bookable personnel (TAKATA, carrosserie, …) and trigger "CCS indisponible". */
export function firstLinkedReceptionnaireId(
  orgReceptions: { servicebox_receptionnaire_id?: string }[] | undefined,
  receptionnaires: AgendaOption[],
): string {
  const linked = (orgReceptions || [])
    .map((r) => r.servicebox_receptionnaire_id)
    .find((id) => id && receptionnaires.some((rr) => rr.id === id));
  return linked || receptionnaires[0]?.id || "";
}

export interface TransferValidation {
  missingFields: string[];
  datesOutOfOrder: boolean;
  canTransfer: boolean;
}

/** Resolve the ServiceBox point-de-vente (pdv_id) for an ordre's vehicle brand, matching
 *  ServiceBoxTransferModal: the vehicle's site, else the Peugeot site, else the org default. */
export function resolvePdvId(
  sites: OrgServiceBoxSite[] | undefined,
  vehicleMarque: string | undefined,
  fallbackSiteCode?: string,
): string {
  const autoSite = detectSite(sites, vehicleMarque);
  const peugeotSite = (sites || []).find(
    (s) => normaliseMarque(s.marque).includes("PEUG") || normaliseMarque(s.label).includes("PEUG"),
  );
  return autoSite?.pdv_id || peugeotSite?.pdv_id || fallbackSiteCode || "";
}

/** Block transfer when essential info is missing — otherwise ServiceBox would receive an
 *  empty/invalid OR. Mirrors ServiceBoxTransferModal's `missingFields`/`canTransfer`.
 *  `requireDossierType` can be turned off for the batch flow, which doesn't load each
 *  ordre's parent dossier (the dossier type isn't part of the proxy payload). */
export function validateOrdreForTransfer(
  ordre: Ordre,
  dossierType?: DossierType,
  opts?: { requireDossierType?: boolean },
): TransferValidation {
  const missingFields: string[] = [];
  if (opts?.requireDossierType !== false && !dossierType) missingFields.push("Type de dossier");
  if (!(ordre.vehicle?.modele || ordre.modele)) missingFields.push("Modèle du véhicule");
  if (!(ordre.immatriculation || ordre.vehicle?.plate)) missingFields.push("Immatriculation");
  // Only "inclus" interventions are billed on this RDV; "à prévoir" lines belong on the dossier.
  // A campaign line goes as a « Campagne » panier line (campagnes), not as a work line.
  if (!ordre.interventions || ordre.interventions.filter((i) => !isAPrevoir(i) && !i.recall_campaign_code).length === 0) {
    missingFields.push("Au moins une intervention incluse");
  }
  if (!ordre.reception_date?.seconds) missingFields.push("Date de réception");
  if (!ordre.restitution?.seconds) missingFields.push("Date de restitution");
  const datesOutOfOrder =
    !!ordre.reception_date?.seconds &&
    !!ordre.restitution?.seconds &&
    ordre.restitution.seconds < ordre.reception_date.seconds;
  return { missingFields, datesOutOfOrder, canTransfer: missingFields.length === 0 && !datesOutOfOrder };
}

/** Detected manufacturer campaigns → « Campagne » lines in the ServiceBox panier. The SB-*
 *  entries are flag-only (campaign signalled without its code) and treated ones are done. */
export function buildCampagnes(ordre: Ordre): NonNullable<CreateRdvParams["campagnes"]> {
  return (ordre.recall_campaigns || [])
    .filter((c) => c.code && !c.code.toUpperCase().startsWith("SB-") && (c.status || "detected") === "detected")
    .map((c) => ({ code: c.code, libelle: c.libelle || "" }));
}

/** Build the ServiceBox `travaux` work-line array from the ordre's included interventions. */
export function buildTravaux(ordre: Ordre, forfaitByLabel: Record<string, string>): CreateRdvParams["travaux"] {
  // ServiceBox intervention "type": "5" = Forfait Local when a forfait code is present
  // (denormalised on the ordre, or resolved from the catalog by name), otherwise left
  // empty so ServiceBox defaults to "Standard" (1). Exclude "à prévoir" lines, and the
  // campaign lines added on detection: those reach ServiceBox through `campagnes`
  // (buildCampagnes) as « Campagne » panier lines — sent here too they would be doubled.
  return ordre.interventions.filter((i) => !isAPrevoir(i) && !i.recall_campaign_code).map((i) => {
    const forfait = i.forfait_code || forfaitByLabel[normaliseLabel(i.label || "")];
    return {
      nom: interventionFullLabel(i) || "Intervention",
      duree: (i.estimated_hours || 0.5).toFixed(2),
      ...(forfait ? { type: "5", forfait_code: forfait } : {}),
    };
  });
}

export interface BuildCreateRdvParamsArgs {
  ordre: Ordre;
  /** The ordre's parent dossier, if loaded — its primary contact is authoritative for client data. */
  parentDossier?: Dossier | null;
  creds: ServiceBoxCredentials;
  receptionnaireId: string;
  restitutionReceptionnaireId?: string;
  equipeId: string;
  pdvId: string;
  /** Catalog forfait codes keyed by normalised intervention name (fallback for older ordres). */
  forfaitByLabel?: Record<string, string>;
}

/**
 * Build the exact `CreateRdvParams` payload the proxy expects. Kept byte-for-byte in step
 * with ServiceBoxTransferModal.handleTransfer — the recovery batch must produce the same
 * payload the working single-transfer produces. Does NOT set skip_alpha; the caller picks
 * createRdv (with OR) vs createRdvServiceBoxOnly (pre-transfer, no OR).
 */
/**
 * Pas de VIN sur l'ordre : la fiche véhicule de la plaque le porte souvent (import Alpha).
 * Sans lui ServiceBox ouvre un dossier sans VIN à ressaisir (Titouan, 2026-10-13).
 * Même règle que le backend (sb_pretransfer._with_vehicle) ; un VIN déjà là n'est jamais remplacé.
 */
export async function withVinFromPlate<T extends { vin?: string; immatriculation?: string }>(
  params: T,
  orgId?: string,
): Promise<T> {
  if (params.vin || !params.immatriculation) return params;
  const norm = (p?: string) => (p || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  try {
    const found = await clientVehiclesApi.search({ plate: params.immatriculation, org_id: orgId, limit: 5 });
    const vin = found.find((v) => norm(v.plate) === norm(params.immatriculation) && v.vin)?.vin;
    if (!vin) return params;
    // Le VIN retrouvé dit aussi la marque : le code ServiceBox suit (cf. vehicleMarque).
    const brand = VIN_BRANDS[vin.replace(/\s/g, "").toUpperCase().slice(0, 3)];
    return brand && "marque" in params ? { ...params, vin, marque: marqueCode(brand) } : { ...params, vin };
  } catch {
    return params;
  }
}

/**
 * Pas de n° Alpha sur le contact : celui que la copie du fichier client Alpha donne avec
 * certitude (même nom + même téléphone ou plaque). Sans lui, un client dont le téléphone a
 * changé devient un second client dans Alpha. Même règle que le backend
 * (sb_pretransfer.alpha_number_from_copy) ; jamais bloquant (2,5 s au plus).
 */
export async function withAlphaNumber<T extends { client_dms_id?: string; nom?: string; prenom?: string; tel_mobile?: string; immatriculation?: string }>(
  params: T,
  orgId?: string,
): Promise<T> {
  if (params.client_dms_id || !params.nom) return params;
  try {
    const { data } = await api.get("/alpha-dms/clients-match", {
      params: { nom: params.nom, prenom: params.prenom || "", phone: params.tel_mobile || "", plate: params.immatriculation || "", ...(orgId ? { org_id: orgId } : {}) },
      timeout: 2500,
    });
    return data?.match?.numero ? { ...params, client_dms_id: data.match.numero } : params;
  } catch {
    return params;
  }
}

export function buildCreateRdvParams({
  ordre,
  parentDossier,
  creds,
  receptionnaireId,
  restitutionReceptionnaireId,
  equipeId,
  pdvId,
  forfaitByLabel = {},
}: BuildCreateRdvParamsArgs): CreateRdvParams {
  // Carvis is authoritative for the client. Prefer the canonical dossier contact (latest
  // edit) over the ordre's denormalised copy.
  const primaryContact = ordre.contacts?.find((c) => c.is_primary) || ordre.contacts?.[0];
  const dossierPrimary = parentDossier?.contacts?.find((c) => c.is_primary) || parentDossier?.contacts?.[0];
  // Le client est celui choisi à la prise de RDV (la société), le téléphone reste le mobile
  // du conducteur, contact principal (validé par Jérôme le 2026-10-15).
  const booked = bookedContact(parentDossier?.contacts, ordre.name);
  const identity = booked
    ? (() => {
        const id = sbClientIdentity(parentDossier, booked, ordre.name || "");
        return id.prenom ? id : { ...id, civilite_id: "4" };  // sans prénom : une société, jamais « M. »
      })()
    : sbClientIdentity(parentDossier, {
        nom: dossierPrimary?.nom || primaryContact?.nom,
        prenom: dossierPrimary?.prenom || primaryContact?.prenom,
        civilite: dossierPrimary?.civilite || primaryContact?.civilite,
      }, ordre.name || "");
  // Structured fields first (saved from the contact form, Alpha-shaped); the one-line
  // `adresse` split heuristically only for contacts that pre-date them.
  const structured = [booked, dossierPrimary, primaryContact].find((c) => c && (c.adresse_rue || c.code_postal || c.ville));
  const addr = structured
    ? { rue: structured.adresse_rue || "", cp: structured.code_postal || "", ville: structured.ville || "" }
    : parseAddress(booked?.adresse || dossierPrimary?.adresse || primaryContact?.adresse);
  const vehicle = ordre.vehicle;
  // MEC au jour près, sinon rien : Alpha l'imprime sur l'OR d'un véhicule qu'il ne connaît
  // pas encore. Une année seule n'est pas envoyée (mêmes règles que sb_pretransfer).
  const mec = mecParts(vehicle?.mec);

  return {
    ...creds,
    date: formatDateYYYYMMDD(ordre.reception_date),
    heure: formatTimeHHMM(ordre.reception_date),
    restitution_date: formatDateYYYYMMDD(ordre.restitution),
    restitution_heure: formatTimeHHMM(ordre.restitution),
    receptionnaire_id: receptionnaireId,
    restitution_receptionnaire_id: restitutionReceptionnaireId || receptionnaireId,
    equipe_id: equipeId,
    // Already on ServiceBox → update the existing RDV (avoids the duplicate-create
    // "CCS indisponible" conflict and pushes updated client data).
    ...(ordre.servicebox_rdv_id ? { rdv_id: ordre.servicebox_rdv_id } : {}),
    ...(ordre.servicebox_dossier_id ? { dossier_id: ordre.servicebox_dossier_id } : {}),
    nom: identity.nom,
    prenom: identity.prenom,
    tel_mobile: booked
      ? sbPhone(dossierPrimary, primaryContact, booked)
      : (dossierPrimary?.phone || primaryContact?.phone || "").replace(/\s/g, ""),
    email: booked?.email || dossierPrimary?.email || primaryContact?.email || "",
    civilite_id: identity.civilite_id,
    // The civilité Carvis really knows (civilite_id falls back to M.): the robot sets it on a
    // client Alpha creates from this transfer.
    client_civilite: knownCivilite(booked || dossierPrimary || primaryContact, identity.civilite_id),
    // N° client Alpha déjà connu : sans lui, un client au téléphone changé devenait un
    // nouveau client Alpha, en double (TRANSPORTS GEANT, 2026-10-15).
    client_dms_id: booked?.dms_id || dossierPrimary?.dms_id || primaryContact?.dms_id || "",
    adresse_rue: addr.rue,
    adresse_code_postal: addr.cp,
    adresse_ville: addr.ville,
    immatriculation: ordre.immatriculation || vehicle?.plate || "",
    vin: vehicle?.vin || "",
    // Brand *code* that ServiceBox files the RDV under (rdvDto.rdvMarque). Without this the
    // proxy defaults to "AC" (Citroën), so every vehicle was created as CITROEN.
    marque: marqueCode(vehicleMarque(vehicle)),
    marque_libelle: vehicle?.marque || vehicleMarque(vehicle),
    ldp_libelle: vehicle?.modele || ordre.modele || "",
    kilometrage: vehicle?.km?.toString() || ordre.booking_request?.km || "",
    mec_day: mec?.day || "",
    mec_month: mec?.month || "",
    mec_year: mec?.year || "",
    travaux: buildTravaux(ordre, forfaitByLabel),
    campagnes: buildCampagnes(ordre),
    pdv_id: pdvId || "011622H",
  };
}
/** Le client d'un ordre déjà transféré vers Alpha a changé dans Carvis : Alpha, lui, ne le
 *  saura pas. Aucune voie ne lui renvoie un client après l'OR (renvoyer le dossier crée un
 *  2e OR) : la fiche Alpha se corrige à la main. `null` quand il n'y a pas encore d'OR —
 *  le client partira alors avec le transfert. Héric 2026-10-16. */
export function alphaClientNotUpdatedNotice(
  ordre: Pick<Ordre, "or_number" | "alpha_or_number"> | null | undefined,
): string | null {
  const or = ordre?.or_number || ordre?.alpha_or_number;
  if (!or) return null;
  return `Client modifié dans Carvis, mais pas dans Alpha : l'OR ${or} existe déjà. Reportez la modification sur la fiche client Alpha.`;
}
